/**
 * Shapes and wording shared by the wallet alert screens.
 *
 * "Customer" in these screens is a merchant: on this platform the wallet
 * belongs to the merchant account, so that is who an alert goes to.
 */

import { Badge } from '../../../components/ui.js'
import { money } from '../../../lib/format.js'

export type RuleStatus = 'draft' | 'active' | 'paused'
export type Priority = 'low' | 'medium' | 'high' | 'critical'
export type TriggerType = 'below' | 'above' | 'range' | 'depleted' | 'usage_over' | 'no_recharge_days'
export type Routing = 'whatsapp' | 'email' | 'both' | 'whatsapp_then_email' | 'email_then_whatsapp'
export type ScheduleType = 'immediate' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'once'

export interface Condition {
  field: string
  op: string
  value: string | number | boolean | null
}

export interface ConditionGroup {
  combinator: 'and' | 'or'
  conditions: Condition[]
}

export interface RuleForm {
  name: string
  description: string
  category: string
  priority: Priority
  status: 'draft' | 'active'
  triggerType: TriggerType
  thresholdAmount: number | null
  thresholdMax: number | null
  currency: string | null
  triggerConfig: { usageWindowDays: number; days: number; recoveryAmount: number | null }
  audience: {
    mode: 'all' | 'specific' | 'filters'
    merchantIds: string[]
    combinator: 'and' | 'or'
    groups: ConditionGroup[]
    includeInactive: boolean
  }
  channels: { routing: Routing; fromName: string; replyTo: string }
  whatsappTemplateId: string | null
  emailTemplateId: string | null
  schedule: {
    type: ScheduleType
    time: string
    weekdays: number[]
    dayOfMonth: number
    startAt: string | null
    endAt: string | null
    timezone: string
    quietHours: { enabled: boolean; start: string; end: string }
    businessDaysOnly: boolean
    maxPerRun: number
  }
  frequency: { repeat: 'once_per_crossing' | 'repeat'; minIntervalHours: number; cooldownHours: number; maxPerDay: number }
}

export interface RuleCounts {
  sent: number
  failed: number
  skipped: number
  queued: number
}

export interface Rule extends Omit<RuleForm, 'status'> {
  id: string
  status: RuleStatus
  whatsappTemplateName: string | null
  emailTemplateName: string | null
  nextRunAt: string | null
  lastRunAt: string | null
  lastTriggeredAt: string | null
  createdAt: string
  updatedAt: string
  counts: RuleCounts
}

export interface Template {
  id: string
  name: string
  channel: 'email' | 'whatsapp'
  language: string
  subject: string | null
  body: string
  textBody: string | null
  variables: string[]
  providerTemplateId: string | null
  approvalStatus: string
  version: number
  status: 'draft' | 'active'
  updatedAt: string
  usedBy?: number
}

export interface Overview {
  enabled: boolean
  timezone: string
  supportUrl: string
  channels: { email: boolean; whatsapp: boolean }
  stats: {
    totalRules: number
    activeRules: number
    sentToday: number
    sentChange: number | null
    pending: number
    failedToday: number
    failedChange: number | null
    merchantsBelow: number
  }
}

export interface Issue {
  level: 'error' | 'warning'
  field: string
  message: string
}

export function defaultRule(timezone = 'Asia/Kolkata'): RuleForm {
  return {
    name: '',
    description: '',
    category: 'low_balance',
    priority: 'medium',
    status: 'draft',
    triggerType: 'below',
    thresholdAmount: 500,
    thresholdMax: null,
    currency: null,
    triggerConfig: { usageWindowDays: 30, days: 30, recoveryAmount: null },
    audience: { mode: 'all', merchantIds: [], combinator: 'and', groups: [], includeInactive: false },
    channels: { routing: 'email', fromName: '', replyTo: '' },
    whatsappTemplateId: null,
    emailTemplateId: null,
    schedule: {
      type: 'daily',
      time: '10:00',
      weekdays: [1],
      dayOfMonth: 1,
      startAt: null,
      endAt: null,
      timezone,
      quietHours: { enabled: false, start: '21:00', end: '09:00' },
      businessDaysOnly: false,
      maxPerRun: 500,
    },
    frequency: { repeat: 'once_per_crossing', minIntervalHours: 24, cooldownHours: 0, maxPerDay: 1 },
  }
}

/** A saved rule back into the editable form. */
export function formFromRule(rule: Rule): RuleForm {
  return {
    name: rule.name,
    description: rule.description ?? '',
    category: rule.category,
    priority: rule.priority,
    status: rule.status === 'active' ? 'active' : 'draft',
    triggerType: rule.triggerType,
    thresholdAmount: rule.thresholdAmount,
    thresholdMax: rule.thresholdMax,
    currency: rule.currency,
    triggerConfig: rule.triggerConfig,
    audience: rule.audience,
    channels: rule.channels,
    whatsappTemplateId: rule.whatsappTemplateId,
    emailTemplateId: rule.emailTemplateId,
    schedule: rule.schedule,
    frequency: rule.frequency,
  }
}

export const PRIORITY_LABEL: Record<Priority, string> = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' }
const PRIORITY_TONE: Record<Priority, string | undefined> = { low: undefined, medium: 'brand', high: 'warn', critical: 'failed' }

export function PriorityBadge({ priority }: { priority: Priority }): React.ReactNode {
  return <Badge tone={PRIORITY_TONE[priority]}>{PRIORITY_LABEL[priority]}</Badge>
}

const STATUS_TONE: Record<string, string | undefined> = {
  active: 'ok',
  paused: 'warn',
  draft: undefined,
  sent: 'ok',
  failed: 'failed',
  queued: 'accent',
  processing: 'accent',
  skipped: undefined,
  suppressed: undefined,
  cancelled: undefined,
}

export function StatusBadge({ status }: { status: string }): React.ReactNode {
  return (
    <Badge tone={STATUS_TONE[status]} dot>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  )
}

export const ROUTING_LABEL: Record<Routing, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  both: 'WhatsApp + Email',
  whatsapp_then_email: 'WhatsApp → Email fallback',
  email_then_whatsapp: 'Email → WhatsApp fallback',
}

export const TRIGGERS: Array<{ key: TriggerType | string; label: string; description: string; available: boolean }> = [
  { key: 'below', label: 'Balance falls below', description: 'Below an amount you set.', available: true },
  { key: 'depleted', label: 'Wallet depleted', description: 'At ₹0 (or a floor you set) or below.', available: true },
  { key: 'range', label: 'Balance within a range', description: 'Between two amounts, inclusive.', available: true },
  { key: 'above', label: 'Balance rises above', description: 'Recovery notice once topped up.', available: true },
  { key: 'usage_over', label: 'Usage reaches a threshold', description: 'Debits over a trailing window.', available: true },
  { key: 'no_recharge_days', label: 'No recharge for N days', description: 'Since the last top-up (or joining).', available: true },
  { key: 'equals', label: 'Balance equals', description: 'Not offered: an exact running balance is never what is meant.', available: false },
  { key: 'percent', label: 'Percentage of initial balance', description: 'Unavailable: wallets have no configured initial balance.', available: false },
  { key: 'recharge', label: 'Recharge successful', description: 'Unavailable: recharge events are not wired to alerts yet.', available: false },
]

export function triggerText(rule: Pick<RuleForm, 'triggerType' | 'thresholdAmount' | 'thresholdMax' | 'currency' | 'triggerConfig'>): string {
  const currency = rule.currency ?? 'INR'
  const amount = rule.thresholdAmount !== null ? money(rule.thresholdAmount, currency) : '—'
  switch (rule.triggerType) {
    case 'below':
      return `Balance < ${amount}`
    case 'above':
      return `Balance > ${amount}`
    case 'range':
      return `${amount} – ${rule.thresholdMax !== null ? money(rule.thresholdMax, currency) : '—'}`
    case 'depleted':
      return `Balance ≤ ${money(rule.thresholdAmount ?? 0, currency)}`
    case 'usage_over':
      return `Usage ≥ ${amount} in ${rule.triggerConfig.usageWindowDays}d`
    case 'no_recharge_days':
      return `No recharge for ${rule.triggerConfig.days}d`
  }
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function clock(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number)
  const suffix = h >= 12 ? 'PM' : 'AM'
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${suffix}`
}

export function scheduleText(schedule: RuleForm['schedule']): string {
  const at = clock(schedule.time)
  const business = schedule.businessDaysOnly ? ' on business days' : ''
  switch (schedule.type) {
    case 'immediate':
      return `Continuously (checked every 5 minutes)${business}`
    case 'hourly':
      return `Every hour at :${schedule.time.split(':')[1] ?? '00'}${business}`
    case 'daily':
      return `Every day at ${at}${business}`
    case 'weekly':
      return `Every ${schedule.weekdays.map((d) => WEEKDAY[d]).join(', ') || '—'} at ${at}`
    case 'monthly':
      return `Monthly on day ${schedule.dayOfMonth} at ${at}${business}`
    case 'once':
      return schedule.startAt ? `Once, on ${new Date(schedule.startAt).toLocaleString('en-IN')}` : 'Once (no start set)'
  }
}

function audienceText(audience: RuleForm['audience']): string {
  const state = audience.includeInactive ? 'all' : 'all active'
  if (audience.mode === 'all') return `${state} merchants`
  if (audience.mode === 'specific') return `${audience.merchantIds.length} selected merchant${audience.merchantIds.length === 1 ? '' : 's'}`
  const conditions = audience.groups.reduce((sum, group) => sum + group.conditions.length, 0)
  return `${state} merchants matching ${conditions} filter condition${conditions === 1 ? '' : 's'}`
}

function conditionText(rule: RuleForm): string {
  const currency = rule.currency ?? 'INR'
  const amount = rule.thresholdAmount !== null ? money(rule.thresholdAmount, currency) : '—'
  switch (rule.triggerType) {
    case 'below':
      return `with a wallet balance below ${amount}`
    case 'above':
      return `whose wallet balance has risen above ${amount}`
    case 'range':
      return `with a wallet balance between ${amount} and ${rule.thresholdMax !== null ? money(rule.thresholdMax, currency) : '—'}`
    case 'depleted':
      return `whose wallet is at ${money(rule.thresholdAmount ?? 0, currency)} or below`
    case 'usage_over':
      return `who have used ${amount} or more in the last ${rule.triggerConfig.usageWindowDays} days`
    case 'no_recharge_days':
      return `who have not recharged in ${rule.triggerConfig.days} days`
  }
}

function channelText(routing: Routing): string {
  switch (routing) {
    case 'email':
      return 'Send an email'
    case 'whatsapp':
      return 'Send an approved WhatsApp template'
    case 'both':
      return 'Send an approved WhatsApp template and an email'
    case 'whatsapp_then_email':
      return 'Send an approved WhatsApp template, falling back to email if WhatsApp fails'
    case 'email_then_whatsapp':
      return 'Send an email, falling back to WhatsApp if email fails'
  }
}

function frequencyText(frequency: RuleForm['frequency']): string {
  const parts: string[] = []
  if (frequency.repeat === 'once_per_crossing') parts.push('Alert once per crossing; reset when the condition clears')
  else parts.push(`Repeat no more than once every ${frequency.minIntervalHours} hour${frequency.minIntervalHours === 1 ? '' : 's'} per merchant`)
  if (frequency.cooldownHours > 0) parts.push(`with a ${frequency.cooldownHours}h cooldown`)
  parts.push(`max ${frequency.maxPerDay} per day`)
  return parts.join(', ')
}

/** The plain-English sentence for the whole rule, built from its actual configuration. */
export function ruleSummary(rule: RuleForm): string {
  const schedule = scheduleText(rule.schedule)
  const lead = rule.schedule.type === 'immediate' ? `${schedule}, watch` : `${schedule}, check`
  return `${lead} ${audienceText(rule.audience)} ${conditionText(rule)}. ${channelText(rule.channels.routing)}. ${frequencyText(rule.frequency)}. Times are in ${rule.schedule.timezone}.`
}

export const FILTER_FIELDS: Array<{ key: string; label: string; kind: 'number' | 'text' | 'enum' | 'date' | 'bool'; options?: string[] }> = [
  { key: 'balance', label: 'Wallet balance', kind: 'number' },
  { key: 'status', label: 'Account status', kind: 'enum', options: ['active', 'paused'] },
  { key: 'plan', label: 'Plan (segment)', kind: 'enum', options: ['starter', 'professional', 'scale'] },
  { key: 'currency', label: 'Currency', kind: 'enum', options: ['INR', 'USD'] },
  { key: 'has_email', label: 'Email available', kind: 'bool' },
  { key: 'email_verified', label: 'Verified email', kind: 'bool' },
  { key: 'has_whatsapp', label: 'WhatsApp number available', kind: 'bool' },
  { key: 'last_recharge_at', label: 'Last recharge', kind: 'date' },
  { key: 'last_transaction_at', label: 'Last wallet transaction', kind: 'date' },
  { key: 'total_recharge', label: 'Total recharged', kind: 'number' },
  { key: 'total_usage', label: 'Total usage', kind: 'number' },
  { key: 'joined_at', label: 'Registration date', kind: 'date' },
  { key: 'merchant_id', label: 'Merchant ID', kind: 'number' },
  { key: 'name', label: 'Merchant name', kind: 'text' },
  { key: 'email', label: 'Email address', kind: 'text' },
  { key: 'phone', label: 'Phone number', kind: 'text' },
]

export const OPS: Record<string, Array<{ key: string; label: string }>> = {
  number: [
    { key: 'lt', label: 'is less than' },
    { key: 'lte', label: 'is at most' },
    { key: 'gt', label: 'is more than' },
    { key: 'gte', label: 'is at least' },
    { key: 'eq', label: 'equals' },
    { key: 'neq', label: 'is not' },
  ],
  text: [
    { key: 'contains', label: 'contains' },
    { key: 'not_contains', label: 'does not contain' },
    { key: 'eq', label: 'is' },
    { key: 'neq', label: 'is not' },
    { key: 'is_empty', label: 'is empty' },
  ],
  enum: [
    { key: 'eq', label: 'is' },
    { key: 'neq', label: 'is not' },
  ],
  date: [
    { key: 'within_days', label: 'within the last (days)' },
    { key: 'older_than_days', label: 'more than (days) ago' },
  ],
  bool: [
    { key: 'is_true', label: 'is yes' },
    { key: 'is_false', label: 'is no' },
  ],
}

/** Saves a CSV string the API returned as a file. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}
